import { useEffect, useState } from 'react';
import { adminApi } from '../../lib/api.js';
import { useToast } from '../../hooks/useToast.jsx';
import Button from '../../components/ui/Button.jsx';
import Input from '../../components/ui/Input.jsx';
import Select from '../../components/ui/Select.jsx';
import { Card, CardContent } from '../../components/ui/Card.jsx';
import { cn } from '@/lib/utils';
import { CalendarSync, Loader2 } from 'lucide-react';
import ConfigWarning from '../../components/ui/ConfigWarning.jsx';
import EmailListInput from '../../components/ui/EmailListInput.jsx';
import Modal from '../../components/ui/Modal.jsx';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const TIMEZONE_OPTIONS = [
  { value: 'Asia/Riyadh', label: 'Asia/Riyadh (UTC+3)' },
  { value: 'Asia/Dubai', label: 'Asia/Dubai (UTC+4)' },
  { value: 'Asia/Kolkata', label: 'Asia/Kolkata (UTC+5:30)' },
  { value: 'Europe/London', label: 'Europe/London (UTC+0/+1)' },
  { value: 'Europe/Berlin', label: 'Europe/Berlin (UTC+1/+2)' },
  { value: 'America/New_York', label: 'America/New_York (UTC-5/-4)' },
  { value: 'America/Chicago', label: 'America/Chicago (UTC-6/-5)' },
  { value: 'America/Denver', label: 'America/Denver (UTC-7/-6)' },
  { value: 'America/Los_Angeles', label: 'America/Los_Angeles (UTC-8/-7)' },
];

export default function SettingsPage() {
  const toast = useToast();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [chains, setChains] = useState([]);
  const [resyncPreview, setResyncPreview] = useState(null);
  const [resyncBusy, setResyncBusy] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const res = await adminApi.getSettings();
      setForm(res.settings);
    } catch (err) {
      toast.error(`Failed to load settings: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }

  async function loadGroups() {
    setGroupsLoading(true);
    try {
      const res = await adminApi.listGroups();
      setGroups(res.groups || []);
    } catch (err) {
      toast.error(`Failed to load M365 groups: ${err.message}`);
    } finally {
      setGroupsLoading(false);
    }
  }

  async function handleSyncHolidays() {
    setSyncing(true);
    try {
      const res = await adminApi.syncHolidaysToCalendar();
      if (res.failed > 0) {
        toast.error(`Synced ${res.synced}, failed ${res.failed} of ${res.total} holidays. ${res.errors?.[0] || ''}`);
      } else if (res.synced === 0 && res.skipped > 0) {
        toast.success(`All ${res.skipped} holidays are already synced to the calendar`);
      } else if (res.synced === 0 && res.total === 0) {
        toast.error('No holidays found. Add holidays first.');
      } else {
        toast.success(`Synced ${res.synced} holiday${res.synced !== 1 ? 's' : ''} to the group calendar`);
      }
    } catch (err) {
      toast.error(`Sync failed: ${err.message}`);
    } finally {
      setSyncing(false);
    }
  }

  async function loadChains() {
    try {
      const res = await adminApi.listChains();
      setChains((res.chains || []).filter((c) => c.isActive !== false));
    } catch {}
  }

  useEffect(() => {
    load();
    loadGroups();
    loadChains();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  function toggleWorkingDay(day) {
    setForm((f) => {
      const days = f.defaultWorkingDays || [];
      return {
        ...f,
        defaultWorkingDays: days.includes(day)
          ? days.filter((d) => d !== day)
          : [...days, day].sort(),
      };
    });
  }

  async function handleSave() {
    setSaving(true);
    try {
      await adminApi.updateSettings(form);
      toast.success('Settings saved');
    } catch (err) {
      toast.error(`Save failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  }

  async function openResyncPreview() {
    setResyncBusy(true);
    try {
      const res = await adminApi.previewCalendarResync();
      setResyncPreview(res);
    } catch (err) {
      toast.error(`Couldn't load resync preview: ${err.message}`);
    } finally {
      setResyncBusy(false);
    }
  }

  async function confirmResync() {
    setResyncBusy(true);
    try {
      const res = await adminApi.runCalendarResync();
      const total = res.totalCreated || 0;
      const errStr = res.errorCount ? `, ${res.errorCount} failed` : '';
      toast.success(`Outlook resync: ${total} event${total === 1 ? '' : 's'} created${errStr}`);
      setResyncPreview(null);
    } catch (err) {
      toast.error(`Resync failed: ${err.message}`);
    } finally {
      setResyncBusy(false);
    }
  }

  if (loading || !form) {
    return (
      <div className="flex items-center justify-center p-8 text-muted-foreground">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
        <span className="ml-3 text-sm">Loading...</span>
      </div>
    );
  }

  return (
    <div className="max-w-2xl">
      {!form.notificationFromEmail && (
        <ConfigWarning message="No notification sender email configured" detail="Email notifications (approvals, rejections, reminders) will not be sent until you set a sender email below." />
      )}
      {!form.appBaseUrl && form.notificationFromEmail && (
        <ConfigWarning message="App URL not set" detail="Email links will point to localhost. Set the App URL below so email buttons work correctly." />
      )}
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground">System-wide configuration for LeavePortal.</p>
      </div>

      <div className="flex flex-col gap-6">
        <Section title="Company">
          <Input
            label="Company name"
            value={form.companyName || ''}
            onChange={(e) => set('companyName', e.target.value)}
          />
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Fiscal year start (MM-DD)"
              value={form.fiscalYearStart || ''}
              onChange={(e) => set('fiscalYearStart', e.target.value)}
              placeholder="01-01"
            />
            <Select
              label="Default timezone"
              value={form.defaultTimezone || 'Asia/Riyadh'}
              onChange={(e) => set('defaultTimezone', e.target.value)}
              options={TIMEZONE_OPTIONS}
            />
          </div>
        </Section>

        <Section title="Default Working Days">
          <div className="flex flex-wrap gap-2">
            {DAY_LABELS.map((label, i) => {
              const active = (form.defaultWorkingDays || []).includes(i);
              return (
                <button
                  key={i}
                  onClick={() => toggleWorkingDay(i)}
                  className={cn(
                    'rounded-md border px-4 py-2 text-sm transition-colors',
                    active
                      ? 'border-primary bg-primary/10 font-semibold text-primary'
                      : 'border-input bg-background text-muted-foreground hover:bg-muted'
                  )}
                >
                  {label}
                </button>
              );
            })}
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Selected days are working days. Leave requests will only count these days.
          </p>
        </Section>

        <Section title="Email & Calendar Mailbox">
          <Input
            label="Notification sender email"
            value={form.notificationFromEmail || ''}
            onChange={(e) => set('notificationFromEmail', e.target.value)}
            placeholder="e.g. hr@profun.com"
          />
          <div className="rounded-md border border-border bg-muted/30 p-3 text-xs text-muted-foreground leading-relaxed space-y-1.5">
            <p><strong className="text-foreground">This single mailbox does two jobs:</strong></p>
            <p>1. <strong>Sends all notification emails</strong> (leave submissions, approvals, rejections, reminders, travel requests).</p>
            <p>2. <strong>Hosts the shared team calendar</strong> — approved leave is written as events on this mailbox's calendar, and everyone subscribes to it.</p>
            <p className="pt-1"><strong className="text-foreground">Requirements:</strong> a real Microsoft 365 mailbox in your tenant (a licensed user or a shared mailbox works). The KSA ProHR app registration needs these <em>application</em> permissions with admin consent: <code className="text-foreground">Mail.Send</code>, <code className="text-foreground">Calendars.ReadWrite</code>, <code className="text-foreground">MailboxSettings.ReadWrite</code>, <code className="text-foreground">User.Read.All</code>, <code className="text-foreground">Directory.Read.All</code>, <code className="text-foreground">Group.Read.All</code>.</p>
            <p><strong className="text-foreground">No inbox monitoring required</strong> — KSA ProHR only sends; replies go to whoever triggered the notification. Leave blank to disable email + calendar sync entirely.</p>
          </div>
          <Input
            label="App URL (for email links)"
            value={form.appBaseUrl || ''}
            onChange={(e) => set('appBaseUrl', e.target.value)}
            placeholder="e.g. https://prohr.profun.com"
          />
          <p className="text-xs text-muted-foreground">
            Links in notification emails point to this URL. Use the public domain your team actually
            uses — <strong className="text-foreground">not</strong> the <code>*.azurestaticapps.net</code> default,
            which breaks sign-in when opened from an email. Leave blank for localhost during development.
          </p>
        </Section>

        <Section title="Approval Reminders">
          <label className="flex cursor-pointer items-center gap-2.5 rounded-md border border-border bg-card p-3">
            <input
              type="checkbox"
              checked={form.approvalReminderEnabled !== false}
              onChange={(e) => set('approvalReminderEnabled', e.target.checked)}
            />
            <div>
              <div className="text-sm font-medium">Send approval reminders</div>
              <div className="text-xs text-muted-foreground">
                Automatically remind approvers about pending requests.
              </div>
            </div>
          </label>
          {form.approvalReminderEnabled !== false && (
            <>
              <p className="text-xs text-muted-foreground">
                Each approver gets a single digest email at ~9am in their own
                local timezone (detected automatically), listing every leave or
                expense request still waiting on them.
              </p>
              <div className="pt-1">
                <Button
                  variant="secondary"
                  onClick={async () => {
                    if (!confirm('Send approver-reminder digest emails now to every approver with a pending queue?')) return;
                    try {
                      const res = await adminApi.sendExpiryReminders(['approvers']);
                      const r = res?.result?.approvers || {};
                      if (r.skipped) toast.success(`Approver reminders: ${r.skipped}`);
                      else toast.success(`Approver reminders: emailed ${r.sent || 0} approver${(r.sent || 0) === 1 ? '' : 's'}`);
                    } catch (err) {
                      toast.error(`Send failed: ${err.message}`);
                    }
                  }}
                >
                  Send approver reminders now
                </Button>
              </div>
            </>
          )}

          <label className="flex cursor-pointer items-center gap-2.5 rounded-md border border-border bg-card p-3 mt-2">
            <input
              type="checkbox"
              checked={form.notifyApproverOnSubmit === true}
              onChange={(e) => set('notifyApproverOnSubmit', e.target.checked)}
            />
            <div>
              <div className="text-sm font-medium">Also email approvers instantly on each submission</div>
              <div className="text-xs text-muted-foreground">
                Off (recommended): approvers only get the single daily digest at 9am in their own
                local time — no per-request emails. On: the relevant approver is also emailed the moment a leave or expense
                request is submitted or advances to their step. Submitters always get an instant
                confirmation either way.
              </div>
            </div>
          </label>
        </Section>

        <Section title="Travel">
          <Input
            label="Travel manager email"
            value={form.travelManagerEmail || ''}
            onChange={(e) => set('travelManagerEmail', e.target.value)}
            placeholder="e.g. officemanager@profun.com"
          />
          <p className="text-xs text-muted-foreground">
            Travel requests will be emailed to this address. The requester is CC'd on all emails.
          </p>
        </Section>

        <Section title="Contract Expiry Reminders">
          <label className="flex cursor-pointer items-center gap-2.5 rounded-md border border-border bg-card p-3">
            <input
              type="checkbox"
              checked={form.contractExpiryReminderEnabled !== false}
              onChange={(e) => set('contractExpiryReminderEnabled', e.target.checked)}
            />
            <div>
              <div className="text-sm font-medium">Enable contract expiry reminders</div>
              <div className="text-xs text-muted-foreground">
                When enabled, the daily reminder timer emails the recipients
                below for any contract within the reminder window or already
                expired. The Contracts page also gets a "Send reminders now"
                button for manual sends.
              </div>
            </div>
          </label>
          {form.contractExpiryReminderEnabled !== false && (
            <>
              <Input
                label="Reminder window (days before expiry)"
                type="number"
                value={form.contractExpiryReminderDays ?? 30}
                onChange={(e) => set('contractExpiryReminderDays', Number(e.target.value) || 30)}
              />
              <EmailListInput
                label="Notification recipients"
                value={form.contractExpiryRecipients || []}
                onChange={(v) => set('contractExpiryRecipients', v)}
                placeholder="hr@company.com"
                helpText="One or more email addresses. Press Enter or comma to add. Empty = no emails sent."
              />
            </>
          )}
        </Section>

        <Section title="Document Expiry Reminders">
          <label className="flex cursor-pointer items-center gap-2.5 rounded-md border border-border bg-card p-3">
            <input
              type="checkbox"
              checked={form.documentExpiryReminderEnabled !== false}
              onChange={(e) => set('documentExpiryReminderEnabled', e.target.checked)}
            />
            <div>
              <div className="text-sm font-medium">Enable document expiry reminders</div>
              <div className="text-xs text-muted-foreground">
                Same flow as contracts, but for the organizational documents
                tracked on the Documents page (visa, license, ISO certs, etc.).
              </div>
            </div>
          </label>
          {form.documentExpiryReminderEnabled !== false && (
            <>
              <Input
                label="Reminder window (days before expiry)"
                type="number"
                value={form.documentExpiryReminderDays ?? 30}
                onChange={(e) => set('documentExpiryReminderDays', Number(e.target.value) || 30)}
              />
              <EmailListInput
                label="Notification recipients"
                value={form.documentExpiryRecipients || []}
                onChange={(v) => set('documentExpiryRecipients', v)}
                placeholder="ops@company.com"
                helpText="One or more email addresses. Press Enter or comma to add. Empty = no emails sent."
              />
            </>
          )}
        </Section>

        <Section title="Expense Approval">
          <Select
            label="Expense approval chain"
            value={form.expenseApprovalChainId || ''}
            onChange={(e) => set('expenseApprovalChainId', e.target.value)}
            options={[
              { value: '', label: 'None — auto-approve all expenses' },
              ...chains.map((c) => ({ value: c.rowKey, label: c.name || c.rowKey })),
            ]}
          />
          <p className="text-xs text-muted-foreground">
            All expense reports will follow this approval chain when submitted. Configure chains in the Approval Chains page.
          </p>
        </Section>

        <Section title="Visa Approval">
          <Select
            label="Visa approval chain"
            value={form.visaApprovalChainId || ''}
            onChange={(e) => set('visaApprovalChainId', e.target.value)}
            options={[
              { value: '', label: 'Direct manager (default)' },
              ...chains.map((c) => ({ value: c.rowKey, label: c.name || c.rowKey })),
            ]}
          />
          <p className="text-xs text-muted-foreground">
            Entry/Exit visa requests follow this chain. Leave as “Direct manager” to route to each requester's manager.
          </p>
        </Section>

        <Section title="Teamwork Integration (Expense Projects)">
          <p className="text-xs text-muted-foreground">
            Populates the <strong>Project</strong> picker when employees create an expense report,
            pulling the live project list from Teamwork. Leave the API key blank to keep Project as a
            free-text field.
          </p>
          <Input
            label="Teamwork base URL"
            value={form.teamworkBaseUrl || ''}
            onChange={(e) => set('teamworkBaseUrl', e.target.value)}
            placeholder="https://profun.teamwork.com"
          />
          <Input
            label="Teamwork API key"
            type="password"
            value={form.teamworkApiKey || ''}
            onChange={(e) => set('teamworkApiKey', e.target.value)}
            placeholder="Your Teamwork API token"
          />
          <Input
            label="Password (optional)"
            value={form.teamworkApiPassword || ''}
            onChange={(e) => set('teamworkApiPassword', e.target.value)}
            placeholder="Leave blank to use 'x'"
          />
          <p className="text-xs text-muted-foreground">
            Teamwork uses HTTP Basic auth with the API token as the username. The password is
            normally ignored (default <code className="text-foreground">x</code>) — only set it if
            your account requires a specific one. The key is stored server-side and is never sent to
            employees; only the resulting project names appear in the dropdown.
          </p>
        </Section>

        <Section title="Flight Lookup (Travel Requests)">
          <p className="text-xs text-muted-foreground">
            Lets employees auto-fill a flight preference from a flight number on the travel
            request form — the airline, route, and scheduled time are pulled from Aviationstack.
            Leave the key blank to keep the flight fields manual.
          </p>
          <Input
            label="Aviationstack API key"
            type="password"
            value={form.aviationstackApiKey || ''}
            onChange={(e) => set('aviationstackApiKey', e.target.value)}
            placeholder="Your aviationstack.com access key"
          />
          <p className="text-xs text-muted-foreground">
            Free at aviationstack.com (100 lookups/month). Stored server-side, never sent to
            employees. Lookups pre-fill the regular route and time for that flight — staff can
            edit any field, and the date they entered is kept.
          </p>
        </Section>

        <Section title="Team Calendar in Outlook">
          <div className="rounded-md border border-border bg-muted/30 p-3 text-xs text-muted-foreground leading-relaxed space-y-2">
            <p>
              <strong className="text-foreground">There's no separate "team calendar" object.</strong>{' '}
              All approved leave, WFH, and Remote events are written directly to the calendar of
              the notification mailbox configured above
              {form.notificationFromEmail
                ? <> — currently <code className="text-foreground">{form.notificationFromEmail}</code></>
                : <> (set <em>Notification sender email</em> above first)</>}
              . Each person on the team adds that mailbox's calendar as a shared overlay in
              Outlook and sees everyone else's leave/WFH/Remote in real time, including
              modifications and deletions.
            </p>
            <p>
              <strong className="text-foreground">Why this works better than a subscription URL:</strong>{' '}
              Outlook's "Subscribe from web" polls iCal feeds on its own multi-hour schedule and
              is famously poor at honoring deletions. Adding a shared mailbox calendar goes
              through Exchange directly — adds, edits, and removals all sync within seconds.
            </p>
          </div>

          <div className="rounded-md border border-border bg-card p-4 text-sm space-y-3">
            <p className="font-semibold text-foreground">Steps for each team member (one-time)</p>

            <div>
              <p className="font-medium text-foreground mb-1">Outlook desktop (Windows)</p>
              <ol className="list-decimal list-inside text-muted-foreground space-y-0.5 ml-1">
                <li>Switch to <strong>Calendar</strong> view (bottom-left).</li>
                <li>Right-click <strong>My Calendars</strong> in the left pane → <strong>Add Calendar</strong> → <strong>Add from Directory</strong>.</li>
                <li>
                  Search
                  {form.notificationFromEmail
                    ? <> <code className="text-foreground">{form.notificationFromEmail}</code></>
                    : ' the notification mailbox email'}
                  , pick it, click <strong>OK</strong>.
                </li>
                <li>The mailbox calendar shows up under <strong>Shared Calendars</strong>; tick it to overlay on yours.</li>
              </ol>
            </div>

            <div>
              <p className="font-medium text-foreground mb-1">Outlook on the web</p>
              <ol className="list-decimal list-inside text-muted-foreground space-y-0.5 ml-1">
                <li>Open <a className="text-primary underline-offset-2 hover:underline" href="https://outlook.office.com/calendar/" target="_blank" rel="noopener">outlook.office.com</a> and switch to <strong>Calendar</strong>.</li>
                <li><strong>Add calendar</strong> (left pane) → <strong>Add from directory</strong>.</li>
                <li>
                  Search
                  {form.notificationFromEmail
                    ? <> <code className="text-foreground">{form.notificationFromEmail}</code></>
                    : ' the notification mailbox email'}
                  , pick it, choose <strong>People's calendars</strong>, click <strong>Add</strong>.
                </li>
                <li>Tick its checkbox in the calendar list to overlay it.</li>
              </ol>
            </div>

            <div className="pt-1 border-t border-border">
              <p className="text-xs text-muted-foreground">
                <strong className="text-foreground">For Teams meeting scheduling:</strong> no
                action needed. When a leave is approved, the requester's <em>own</em> Outlook
                calendar also gets an out-of-office event added automatically. Teams Scheduling
                Assistant sees them as busy/OOF when you add them to a meeting — no extra setup.
              </p>
            </div>
          </div>

          <div className="rounded-md border border-border bg-card p-4 space-y-2">
            <p className="text-sm font-semibold text-foreground">Resync Outlook calendar</p>
            <p className="text-xs text-muted-foreground">
              Approved leaves and WFH/Remote rows from before the recent calendar-sync fixes
              may not have Outlook events yet. This walks the database and creates missing
              events on the requester's Outlook + the shared mailbox. Rows that already have
              events are left alone — no double-booking. Shows a preview before anything is
              created.
            </p>
            <div>
              <Button variant="secondary" onClick={openResyncPreview} loading={resyncBusy}>
                Check & resync missing events
              </Button>
            </div>
          </div>
        </Section>

        <div className="pt-2">
          <Button onClick={handleSave} loading={saving}>
            Save settings
          </Button>
        </div>
      </div>

      {resyncPreview && (() => {
        const l = resyncPreview.leaves || { rows: 0, personalMissing: 0, groupMissing: 0 };
        const s = resyncPreview.schedules || { rows: 0, personalMissing: 0, groupMissing: 0 };
        const totalMissingEvents =
          (l.personalMissing || 0) + (l.groupMissing || 0)
          + (s.personalMissing || 0) + (s.groupMissing || 0);
        const totalRows = (l.rows || 0) + (s.rows || 0);
        return (
          <Modal
            isOpen
            onClose={() => !resyncBusy && setResyncPreview(null)}
            title="Resync Outlook calendar"
            width="600px"
            footer={
              <>
                <Button variant="ghost" onClick={() => setResyncPreview(null)} disabled={resyncBusy}>
                  Cancel
                </Button>
                <Button
                  onClick={confirmResync}
                  loading={resyncBusy}
                  disabled={totalMissingEvents === 0}
                >
                  Create {totalMissingEvents} missing event{totalMissingEvents === 1 ? '' : 's'}
                </Button>
              </>
            }
          >
            {totalMissingEvents === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                Everything is already in sync — no missing Outlook events found.
              </div>
            ) : (
              <div className="space-y-3 text-sm">
                <p className="text-muted-foreground">
                  Each leave or WFH/Remote day has TWO Outlook events: one on the requester's
                  own calendar (drives Teams scheduler) and one on the shared HR mailbox
                  calendar (the team overlay). A row can have either, both, or neither.
                </p>

                <div className="rounded-md border border-border bg-muted/30 p-3 text-xs space-y-2">
                  <div className="font-semibold text-foreground">Will be created</div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div></div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Personal Outlook</div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Team overlay</div>

                    <div className="text-right text-muted-foreground">Leaves</div>
                    <div className="font-mono font-semibold">{l.personalMissing || 0}</div>
                    <div className="font-mono font-semibold">{l.groupMissing || 0}</div>

                    <div className="text-right text-muted-foreground">WFH / Remote</div>
                    <div className="font-mono font-semibold">{s.personalMissing || 0}</div>
                    <div className="font-mono font-semibold">{s.groupMissing || 0}</div>
                  </div>
                </div>

                <p className="text-xs text-muted-foreground">
                  Across <strong className="text-foreground">{totalRows}</strong> row{totalRows === 1 ? '' : 's'}.
                  Personal events are only created for current or future leaves/days; team-overlay
                  events backfill recent past too so the team view has context.
                </p>

                {resyncPreview.sample?.leaves?.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                      Sample leaves
                    </div>
                    <ul className="text-xs space-y-0.5">
                      {resyncPreview.sample.leaves.map((row) => (
                        <li key={row.rowKey} className="flex justify-between gap-3">
                          <span className="truncate">{row.userEmail} — {row.typeName}</span>
                          <span className="text-muted-foreground font-mono shrink-0">
                            {row.startDate} → {row.endDate}{' '}
                            <span className="text-[10px] uppercase">[{row.missing}]</span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {resyncPreview.sample?.schedules?.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                      Sample WFH / Remote
                    </div>
                    <ul className="text-xs space-y-0.5">
                      {resyncPreview.sample.schedules.map((row) => (
                        <li key={`${row.rowKey}-${row.userEmail}`} className="flex justify-between gap-3">
                          <span className="truncate">
                            {row.userEmail} — {row.type === 'wfh' ? 'WFH' : 'Remote'}
                          </span>
                          <span className="text-muted-foreground font-mono shrink-0">
                            {row.date}{' '}
                            <span className="text-[10px] uppercase">[{row.missing}]</span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <p className="text-xs text-muted-foreground pt-1">
                  Rows that already have both events are skipped. Running this multiple times
                  is safe.
                </p>
              </div>
            )}
          </Modal>
        );
      })()}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="mb-3 border-b border-border pb-1.5 text-sm font-semibold">
          {title}
        </div>
        <div className="flex flex-col gap-3">{children}</div>
      </CardContent>
    </Card>
  );
}
